import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { MdBarChart, MdShowChart, MdStar, MdChevronLeft, MdChevronRight } from 'react-icons/md';

function CountUpStat({ end, duration = 4000, suffix = '%' }) {
  const [count, setCount] = useState(0);
  const elementRef = useRef(null);
  const hasStartedRef = useRef(false);

  useEffect(() => {
    const node = elementRef.current;
    if (!node) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasStartedRef.current) {
            hasStartedRef.current = true;
            const startTime = performance.now();
            const animate = (now) => {
              const elapsed = now - startTime;
              const progress = Math.min(elapsed / duration, 1);
              // ease-out cubic for a satisfying decel at the top
              const eased = 1 - Math.pow(1 - progress, 3);
              setCount(Math.round(eased * end));
              if (progress < 1) requestAnimationFrame(animate);
            };
            requestAnimationFrame(animate);
          }
        });
      },
      { threshold: 0.4 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [end, duration]);

  return (
    <span ref={elementRef} className="countup-number">
      {count}
      {suffix}
    </span>
  );
}

const PARTNERS = [
  {
    name: 'Wone Consulting',
    src: '/wone-consulting-logo.png',
    href: 'https://www.woneconsulting.com.au/',
  },
  {
    name: 'Sunshine Heights Cricket Club',
    src: '/sunshine-heights-logo.jpg',
    href: 'https://www.sunshineheightscc.com.au/',
    round: true,
  },
  {
    name: 'Rotary International',
    src: '/rotary-logo-color-2019-simplified.svg',
    href: 'https://www.rotary.org/en',
  },
  {
    name: 'Pacific Asia Travel Association',
    src: '/Pacific_Asia_Travel_Association_Logo.png',
    href: 'https://www.pata.org/',
  },
  {
    name: 'Agora Food Studio',
    src: '/logoAgoraW.webp',
    href: 'https://www.timorlestefoodlab.com/',
  },
  {
    name: 'Timor-Leste Food Lab',
    src: '/TLFoodLab.webp',
    href: 'https://www.timorlestefoodlab.com/',
  },
];

const PARTNERS_VISIBLE = 4;
const PARTNER_LOOP_AT = PARTNERS.length;

function Home() {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showPlayButton, setShowPlayButton] = useState(true); // Show by default for testing
  const [partnerOffset, setPartnerOffset] = useState(0);
  const [partnerTransition, setPartnerTransition] = useState(true);
  const [partnersAutoRotate, setPartnersAutoRotate] = useState(true);
  const partnerTrackRef = useRef(null);
  const partnerOffsetRef = useRef(0);
  const partnerResettingRef = useRef(false);

  // Duplicate the first VISIBLE partners at the end for a seamless forward loop
  const partnerSlides = [...PARTNERS, ...PARTNERS.slice(0, PARTNERS_VISIBLE)];

  useEffect(() => {
    partnerOffsetRef.current = partnerOffset;
  }, [partnerOffset]);

  const resetPartnerLoop = useCallback(() => {
    if (partnerResettingRef.current) return;
    partnerResettingRef.current = true;
    setPartnerTransition(false);
    setPartnerOffset(0);
    partnerOffsetRef.current = 0;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setPartnerTransition(true);
        partnerResettingRef.current = false;
      });
    });
  }, []);

  const stepPartners = useCallback((direction) => {
    if (partnerResettingRef.current) return;
    const current = partnerOffsetRef.current;

    if (direction > 0) {
      if (current >= PARTNER_LOOP_AT) return;
      const next = current + 1;
      partnerOffsetRef.current = next;
      setPartnerOffset(next);
      return;
    }

    if (current > 0) {
      const next = current - 1;
      partnerOffsetRef.current = next;
      setPartnerOffset(next);
      return;
    }

    // At start: jump to clone index, then animate one step back
    partnerResettingRef.current = true;
    setPartnerTransition(false);
    partnerOffsetRef.current = PARTNER_LOOP_AT;
    setPartnerOffset(PARTNER_LOOP_AT);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setPartnerTransition(true);
        const next = PARTNER_LOOP_AT - 1;
        partnerOffsetRef.current = next;
        setPartnerOffset(next);
        partnerResettingRef.current = false;
      });
    });
  }, []);

  const handlePartnerPrev = () => {
    setPartnersAutoRotate(false);
    stepPartners(-1);
  };

  const handlePartnerNext = () => {
    setPartnersAutoRotate(false);
    stepPartners(1);
  };

  useEffect(() => {
    if (!partnersAutoRotate) return undefined;
    const timer = setInterval(() => {
      stepPartners(1);
    }, 3000);
    return () => clearInterval(timer);
  }, [partnersAutoRotate, stepPartners]);

  useEffect(() => {
    if (partnerOffset !== PARTNER_LOOP_AT) return undefined;
    // Don't auto-reset while manually jumping backward through the clone index
    if (partnerResettingRef.current) return undefined;

    const track = partnerTrackRef.current;
    let didReset = false;
    const doReset = () => {
      if (didReset) return;
      didReset = true;
      resetPartnerLoop();
    };

    const handleTransitionEnd = (event) => {
      if (track && event.target !== track) return;
      if (event.propertyName && event.propertyName !== 'transform') return;
      doReset();
    };

    if (track) track.addEventListener('transitionend', handleTransitionEnd);
    // Fallback if transitionend is skipped (tab backgrounded, reduced motion, etc.)
    const fallback = setTimeout(doReset, 700);

    return () => {
      if (track) track.removeEventListener('transitionend', handleTransitionEnd);
      clearTimeout(fallback);
    };
  }, [partnerOffset, resetPartnerLoop]);

  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      // Simple autoplay attempt
      const attemptPlay = () => {
        console.log('Attempting to play video...');
        video.play()
          .then(() => {
            console.log('Video playing successfully');
            setIsPlaying(true);
            setShowPlayButton(false);
          })
          .catch((error) => {
            console.log('Autoplay prevented:', error);
            setShowPlayButton(true);
            setIsPlaying(false);
          });
      };

      // Try to play after a short delay to ensure video is loaded
      const timer = setTimeout(attemptPlay, 500);
      
      // Also try when video can play
      video.addEventListener('loadeddata', attemptPlay);

      return () => {
        clearTimeout(timer);
        video.removeEventListener('loadeddata', attemptPlay);
      };
    }
  }, []);

  const handlePlayClick = () => {
    const video = videoRef.current;
    if (video) {
      console.log('Manual play clicked');
      video.play()
        .then(() => {
          console.log('Manual play successful');
          setIsPlaying(true);
          setShowPlayButton(false);
        })
        .catch((error) => {
          console.error('Error playing video:', error);
        });
    }
  };

  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content-layout">
          <div className="hero-text">
            <h1>Low Trust. High Cost.</h1>
            <p className="hero-subtitle">
              Your strategy doesn't fail because of poor ideas. It fails because of low trust.
            </p>
            <p className="hero-description">
            At Empathos, we guide high performers to transform into high trust leaders.

            </p>
            <div className="cta-buttons">
              <Link to="/contact" className="cta-primary" onClick={() => setTimeout(() => {
                const mainContent = document.querySelector('.main-content');
                if (mainContent) {
                  mainContent.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
              }, 100)}>Start Assessment</Link>
              <Link to="/about" className="cta-secondary" onClick={() => setTimeout(() => {
                const mainContent = document.querySelector('.main-content');
                if (mainContent) {
                  mainContent.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
              }, 100)}>Learn More</Link>
            </div>
          </div>
          <div className="hero-video-section">
            <div className="video-container">
              <video
                ref={videoRef}
                className="platform-video"
                autoPlay
                loop
                muted
                playsInline
                controls={false}
                preload="metadata"
                onLoadedData={() => console.log('Video loaded')}
                onCanPlay={() => console.log('Video can play')}
                onPlay={() => console.log('Video playing')}
                onError={(e) => console.log('Video error:', e)}
              >
                <source src="/emp_demo_vid.mp4" type="video/mp4" />
                Your browser does not support the video tag.
              </video>
              {showPlayButton && (
                <button className="video-play-button" onClick={handlePlayClick}>
                  <div className="play-icon">▶</div>
                  <span>Watch Demo</span>
                </button>
              )}
            </div>
            <p className="video-caption">See our Trust Intelligence<sup>&trade;</sup> platform in action</p>
          </div>
        </div>
      </section>

      {/* Trust Deficit Section */}
      <section className="trust-deficit-section">
        <div className="section-header">
          <span className="section-tag">The Problem</span>
          <h2>Business teams are operating with an invisible trust deficit.</h2>
        </div>
        <div className="trust-deficit-stats">
          <div className="deficit-stat">
            <CountUpStat end={71} />
            <p className="deficit-label">
              of employees do not trust executive leadership.
            </p>
            <a
              className="deficit-source"
              href="https://www.ddi.com/global-leadership-forecast-2023"
              target="_blank"
              rel="noopener noreferrer"
            >
              Source: DDI, Global Leadership Forecast 2020 &amp; 2023
            </a>
          </div>
          <div className="deficit-stat">
            <CountUpStat end={24} />
            <p className="deficit-label">
              of employees have left a role mainly because they &lsquo;did not feel trusted.&rsquo;
            </p>
            <a
              className="deficit-source"
              href="https://www.pwc.com/sg/en/publications/hopes-and-fears/2023.html"
              target="_blank"
              rel="noopener noreferrer"
            >
              Source: PwC, Global Workforce Hopes and Fears Survey (2022&ndash;2023)
            </a>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="services-section">
        <div className="section-header">
          <span className="section-tag">Our Solution</span>
          <h2>You're brave enough to lead. We help you lead with trust.</h2>
        </div>

        <div className="services-grid">
          <div className="service-card">
            <div className="service-icon">
              <MdBarChart size={40} color="#2B9BCA" />
            </div>
            <h3>Measure</h3>
            <p>Establish your trust baseline and identify hidden leadership trust deficits.</p>
            <div className="service-features">
              <span>Baseline Trust Intelligence&trade; diagnostic</span>
              <span>Leader-Team Trust Deficit Score</span>
              <span>Personalised trust building actions</span>
            </div>
          </div>

          <div className="service-card">
            <div className="service-icon">
              <MdShowChart size={40} color="#2B9BCA" />
            </div>
            <h3>Grow</h3>
            <p>Track trust over time with guided actions and measurable leadership growth.</p>
            <div className="service-features">
              <span>3 x quarterly Trust Intelligence&trade; diagnostic</span>
              <span>Leader-Team Trust Deficit Scores</span>
              <span>Personalised trust building actions</span>
              <span>Full Trust Intelligence&trade; platform access</span>
            </div>
          </div>

          <div className="service-card">
            <div className="service-icon">
              <MdStar size={40} color="#2B9BCA" />
            </div>
            <h3>Transform</h3>
            <p>Embed high-trust leadership through ongoing measurement and guidance.</p>
            <div className="service-features">
              <span>5 x quarterly Trust Intelligence&trade; diagnostic</span>
              <span>Leader-Team Trust Deficit Scores</span>
              <span>Personalised trust building actions</span>
              <span>Full Trust Intelligence&trade; platform access</span>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Intelligence Section */}
      <section className="trust-intelligence-section">
        <div className="ti-content">
          <div className="ti-text">
            <span className="section-tag">Why Empathos</span>
            <h2>We help leaders measure and close the hidden trust gaps limiting team performance.</h2>
            <p>
              Most leaders trying to improve trust invest in generic solutions like team-building workshops, trust falls, or traditional 360-degree feedback without understanding where trust deficits actually exist between leaders and teams.
          <br></br>
          <br></br>
              <b>Trust Intelligence<sup>&trade;</sup> transforms trust into a measurable, actionable leadership capability.</b>
            </p>
            <div className="empathos-cards">
              <div className="empathos-card">
                <h4>Our Trust Intelligence<sup>&trade;</sup> Model</h4>
                <p>
                  Trust Intelligence<sup>&trade;</sup> scientifically quantifies the Leader&ndash;Team Trust Deficit across:
                </p>
                <ul>
                  <li>3 elements of the Trust Triangle</li>
                  <li>6 dimensions of Intergroup Trust</li>
                  <li>12 essential trust-building skills</li>
                  <li>144 trust signals</li>
                </ul>
              </div>
              <div className="empathos-card">
                <h4>What is the Leader&ndash;Team Trust Deficit?</h4>
                <p>
                  The Leader&ndash;Team Trust Deficit is the measurable gap between a leader&rsquo;s perception of trust and the team&rsquo;s experience of trust, and:
                </p>
                <ul>
                  <li>Reveals where trust may be misaligned between leaders and teams</li>
                  <li>Identifies which trust-building skills are most in need of strengthening or repair</li>
                  <li>Highlights hidden trust gaps limiting retention, alignment, and business growth</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Impact Section */}
      <section className="impact-section">
        <div className="impact-content">
          <h2>Trust changes everything.</h2>
          <div className="impact-stats">
            <div className="stat">
              <div className="stat-number">
                <CountUpStat end={23} />
              </div>
              <div className="stat-label">
                Higher profitability achieved by high-trust, highly engaged teams.
              </div>
              <a
                className="stat-source"
                href="https://www.gallup.com/workplace/349484/state-of-the-global-workplace.aspx"
                target="_blank"
                rel="noopener noreferrer"
              >
                Source: Gallup, State of the Global Workplace
              </a>
            </div>
            <div className="stat">
              <div className="stat-number">
                <CountUpStat end={76} />
              </div>
              <div className="stat-label">
                More employee engagement from high-trust companies.
              </div>
              <a
                className="stat-source"
                href="https://www.ahri.com.au/articles/trust-key-to-high-performance-teams"
                target="_blank"
                rel="noopener noreferrer"
              >
                Source: AHRI, Trust key to high-performance teams
              </a>
            </div>
            <div className="stat">
              <div className="stat-number">
                <CountUpStat end={40} />
              </div>
              <div className="stat-label">Less employee burnout.</div>
              <a
                className="stat-source"
                href="https://www.ahri.com.au/articles/trust-key-to-high-performance-teams"
                target="_blank"
                rel="noopener noreferrer"
              >
                Source: AHRI, Trust key to high-performance teams
              </a>
            </div>
          </div>
          <p className="impact-description">
            People stay, teams thrive, and missions advance. This isn't culture work.
            This is trust work. And it changes everything.
          </p>
        </div>
      </section>

      {/* Partners Section */}
      <section className="partners-section">
        <div className="section-header">
          <span className="section-tag">Our Partners</span>
          <h2>Trusted by mission-driven organizations</h2>
        </div>
        <div className="partners-carousel-wrap">
          <button
            type="button"
            className="partners-nav partners-nav--prev"
            onClick={handlePartnerPrev}
            aria-label="Previous partners"
          >
            <MdChevronLeft size={32} />
          </button>
          <div
            className="partners-carousel"
            aria-roledescription="carousel"
            aria-label="Partner logos"
            style={{
              '--partner-count': partnerSlides.length,
              '--partners-visible': PARTNERS_VISIBLE,
            }}
          >
            <div
              ref={partnerTrackRef}
              className="partners-track"
              style={{
                transform: `translateX(-${(partnerOffset * 100) / partnerSlides.length}%)`,
                transition: partnerTransition ? 'transform 0.6s ease' : 'none',
              }}
            >
              {partnerSlides.map((partner, index) => (
                <div className="partner-slide" key={`${partner.name}-${index}`}>
                  <a
                    href={partner.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`partner-item${partner.round ? ' partner-item--round' : ''}`}
                    aria-label={partner.name}
                  >
                    <img src={partner.src} alt={partner.name} />
                  </a>
                </div>
              ))}
            </div>
          </div>
          <button
            type="button"
            className="partners-nav partners-nav--next"
            onClick={handlePartnerNext}
            aria-label="Next partners"
          >
            <MdChevronRight size={32} />
          </button>
        </div>
      </section>

      {/* CTA Section */}
      <section className="final-cta-section">
        <div className="cta-content">
          <h2>Ready to measure what matters most?</h2>
          <p>Start your Trust Intelligence<sup>&trade;</sup> journey today</p>
          <div className="cta-buttons cta-buttons--center">
            <Link to="/contact" className="cta-primary large" onClick={() => setTimeout(() => {
              const mainContent = document.querySelector('.main-content');
              if (mainContent) {
                mainContent.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }
            }, 100)}>Begin Trust Assessment</Link>
            <Link
              to="/contact?view=talk-about-trust"
              className="cta-primary large"
              onClick={() => setTimeout(() => {
                const mainContent = document.querySelector('.main-content');
                if (mainContent) {
                  mainContent.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
              }, 100)}
            >
              Let&rsquo;s Talk About Trust
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="site-footer">
        <div className="footer-content">
          <div className="footer-main">
            <div className="footer-section">
              <h4>Navigation</h4>
              <nav className="footer-nav">
                <Link to="/" onClick={() => setTimeout(() => {
                  const mainContent = document.querySelector('.main-content');
                  if (mainContent) {
                    mainContent.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }
                }, 100)}>Home</Link>
                <Link to="/about" onClick={() => setTimeout(() => {
                  const mainContent = document.querySelector('.main-content');
                  if (mainContent) {
                    mainContent.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }
                }, 100)}>About</Link>
                <Link to="/contact" onClick={() => setTimeout(() => {
                  const mainContent = document.querySelector('.main-content');
                  if (mainContent) {
                    mainContent.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }
                }, 100)}>Contact</Link>
                <Link to="/login" onClick={() => setTimeout(() => {
                  const mainContent = document.querySelector('.main-content');
                  if (mainContent) {
                    mainContent.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }
                }, 100)}>Login</Link>
              </nav>
            </div>
            
            <div className="footer-section">
              <h4>Connect</h4>
              <div className="social-links">
                <a href="https://www.linkedin.com/company/empathostrustintelligence/" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" fill="currentColor"/>
                  </svg>
                  LinkedIn
                </a>
                <a href="mailto:team@empathos.com.au" target="_blank" rel="noopener noreferrer" aria-label="Email">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" stroke="currentColor" strokeWidth="2" fill="none"/>
                    <polyline points="22,6 12,13 2,6" stroke="currentColor" strokeWidth="2" fill="none"/>
                  </svg>
                  Email
                </a>
              </div>
            </div>
            
            <div className="footer-section">
              <h4>Trust Intelligence<sup>&trade;</sup></h4>
              <div className="definition">
                <div className="definition-term">
                  /trʌst ɪnˈtelɪdʒəns/
                </div>
                <div className="definition-part">
                  <em>noun</em>
                </div>
                <div className="definition-meaning">
                  The measurable capacity to understand and strengthen trust between leaders and their teams.
                </div>
              </div>
            </div>
          </div>
          
          <div className="footer-bottom">
            <div className="copyright">
              <p><em>We acknowledge the lands of the Bidjigal People of the Eora Nation upon whose land we work.</em></p>
              <p>&copy; {new Date().getFullYear()} Empathos. All rights reserved.</p>
            </div>
            <div className="footer-logo">
              <a href="/" aria-label="Return to home">
                <img src="/empy_svg_test.svg" alt="Empathos" className="footer-logo-svg" />
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default Home; 